import { Icon, MenuItem } from "@material-ui/core";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { v4 as uuid } from "uuid";

import { ASSET_FILETYPE, AssetType } from "common/constants";
import {
    ActionPermissionsObject,
    IWorkspaceResourceAsset,
    IWorkspaceResourceFolder,
    PermissionActionType,
    WorkspacePlanTier,
    WorkspaceResourceFolderType,
    WorkspaceResourceType
} from "common/interfaces";
import getObjectHash from "common/utils/getObjectHash";

import SharedAssetsPaneController, { SharedAssetsPaneControllerState } from "js/controllers/SharedAssetsPaneController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import { PresentationListHeader } from "js/editor/PresentationLibrary/PresentationListHeader";
import { SearchAndAdd, SearchAndAddFilterOption } from "js/editor/PresentationLibrary/components/SearchAndAdd";
import {
    buildResourcePaneBreadcrumbData,
    PresentationLibraryPaneBreadcrumb,
} from "js/editor/PresentationLibrary/components/PresentationLibraryPaneBreadcrumb";
import { Layout, PaneBody, PaneContent, ResourcePaneSideBar, SearchBarSort, teamResourceGridProps } from "js/react/views/SharedResources/Components";

import Banner from "js/Components/Banner";
import { Button as AppButton } from "js/Components/Button";
import { Icon as BaiIcon } from "js/Components/Icon";
import UpgradeIcon from "js/Components/UpgradeIcon";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { t } from "js/i18n";
import { app } from "js/namespaces";
import { ShowConfirmationDialog, ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import { Gap5 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { NoMatchNotice, NoTeamAssetNotice, SharedSlideFolderNotice } from "js/react/components/Notice";
import Spinner from "js/react/components/Spinner";
import AddAssetsContainer from "js/react/views/AddAssets/AddAssetsContainer";
import { UIPaneResultsContainer } from "js/react/views/AddSlide/Panes/Components/SearchBox";
import { MultiSelectThumbnailGrid } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import { AssetThumbnail } from "js/react/views/SharedResources/Components/AssetThumbnail";
import { SharedResourceFolderNavigateHandler } from "js/react/views/SharedResources/Components/SharedResourceContextList";
import { selectedClass, SideBarItem } from "js/react/views/SharedResources/Components/WorkspaceResourcesSidebarHelper";
import { useFolderDragItems } from "js/react/views/SharedResources/hooks/useFolderDragItems";
import { folderDropResourceFromWorkspaceResource, moveResourcesToFolder } from "js/react/views/SharedResources/utils/folderDropHelpers";
import { removeResourcesFromFolder } from "js/react/views/SharedResources/utils/removeResourcesFromFolder";
import { BulkAssetUploadDialog, BulkSetTagsDialog } from "js/react/views/TeamResources/dialogs/BulkAssetUploadDialog";
import CreateTeamAssetDialog from "js/react/views/TeamResources/dialogs/CreateTeamAssetDialog";
import TeamAssetSettingsDialog from "js/react/views/TeamResources/dialogs/TeamAssetSettingsDialog";
import { TeamResourceTypes } from "js/react/views/TeamResources/TeamResources";

const logger = getLogger(LogGroup.TEAMS);
const TAG_FILTER_ALL = "__all__";

const ResultsContainer = styled(UIPaneResultsContainer)`
    margin-top: 0;
    padding: 0;
    flex: 1;
    width: 100%;
    min-width: 0;
    min-height: 0;
    box-sizing: border-box;
    overflow-x: hidden;
    overflow-y: auto;
`;

const BulkUploadButton = styled(AppButton)`
    &&& {
        letter-spacing: 0.5px;
        background-color: #ddd;
        color: #444;
        text-align: center;
        /* button/primary box */
        font-family: "Source Sans Pro";
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        line-height: 24px;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        border:none !important;
    }
`;

const UploadFileInput = styled.input`
    display: none;
`;

const SearchControls = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
`;

const AssetActions = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
`;

const CreateAssetButton = styled(AppButton)`
    &&& {
        letter-spacing: 0.5px;
    }
`;

type SharedAssetsPaneProps = WorkspaceControllerState & SharedAssetsPaneControllerState & {
    resourceType: string;
    editable?: boolean;
    addAssetCallback?: (asset: IWorkspaceResourceAsset) => void;
    handleConfirm?: () => void;
    onSelectFolder?: (pane: string, folderId?: string) => void;
    folderId?: string;
};

const sortOptions: SearchBarSort[] = [{
    field: "resource.metadata.name", label: t("Name"), direction: "asc", icon: "sort_by_alpha",
}, {
    field: "resource.createdAt", label: t("Created At"), direction: "desc", icon: "calendar_month",
}, {
    field: "resource.modifiedAt", label: t("Modified At"), direction: "desc", icon: "edit_calendar",
}];

const ListHeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

function mediaFilterForResourceType(resourceType: string) {
    if (resourceType === TeamResourceTypes.TEAM_ASSETS_IMAGES) {
        return "image";
    }
    if (resourceType === TeamResourceTypes.SHARED_ASSETS_LOGOS) {
        return "logo";
    }
    return "__all__";
}

function SharedAssetsPane({
    resourceType: initialResourceType,
    editable,
    addAssetCallback,
    handleConfirm,
    tags,
    fetching,
    data,
    search,
    workspace,
    resourceFolders,
    onSelectFolder,
    folderId,
}: SharedAssetsPaneProps) {
    const [isInitializedForCurrentMount, setIsInitializedForCurrentMount] = useState(false);
    const [sort, setSort] = useState<SearchBarSort>({
        field: "resource.modifiedAt",
        direction: "desc",
    });
    const [selectedTag, setSelectedTag] = useState(TAG_FILTER_ALL);
    const [selectedFolderId, setSelectedFolderId] = useState<string | null>(folderId ?? null);
    const {
        draggedItems,
        handleStartDragItems,
        handleDropDragItems,
        clearDragItems,
        getActiveDragItems,
    } = useFolderDragItems(folderDropResourceFromWorkspaceResource);
    const [resourceType, setResourceType] = useState<string>(initialResourceType);
    const bulkUploadInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setResourceType(initialResourceType);
    }, [initialResourceType]);

    useEffect(() => {
        setSelectedFolderId(folderId ?? null);
    }, [folderId]);

    const controllerSort = [{
        field: sort.field,
        direction: sort.direction,
    }];

    const controllerFilters = (() => {
        const filters = [];
        const mediaFilter = mediaFilterForResourceType(resourceType);

        if (mediaFilter === "__all__") {
            filters.push({
                field: "resource.metadata.type",
                value: ["image", "logo"],
                operator: "in",
            });
        } else {
            filters.push({
                field: "resource.metadata.type",
                value: mediaFilter,
                operator: "eq",
            });
        }

        if (selectedTag !== TAG_FILTER_ALL) {
            filters.push({
                field: "resource.tags",
                operator: "eq",
                value: selectedTag,
            });
        }

        if (selectedFolderId) {
            filters.push({
                field: "resource.folderIds",
                operator: "eq",
                value: selectedFolderId,
            });
        }

        if (!editable) {
            filters.push({
                field: "resource.metadata.isPublished",
                operator: "eq",
                value: true,
            });
        }

        return filters;
    })();

    const tagFilterOptions = useMemo<SearchAndAddFilterOption[]>(() => [
        { field: TAG_FILTER_ALL, label: t("All Tags") },
        ...tags.map(({ tag, count }) => ({
            field: tag,
            label: `${tag} (${count})`,
        })),
    ], [tags]);

    useEffect(() => {
        let isMounted = true;

        void SharedAssetsPaneController.initialize({
            workspaceId: WorkspaceController.workspaceId,
            sort: controllerSort,
            filters: controllerFilters,
            search: { query: "" },
        }).then(() => {
            if (isMounted) {
                setIsInitializedForCurrentMount(true);
            }
        });

        return () => {
            isMounted = false;
            SharedAssetsPaneController.reset();
        };
    }, []);

    const prevSearchStateRef = useRef({
        resourceType,
        selectedTag,
        selectedFolderId,
        sort,
    });

    useEffect(() => {
        const prev = prevSearchStateRef.current;

        if (!_.isEqual(prev.sort, sort)) {
            SharedAssetsPaneController.setSort(controllerSort);
        }
        if (
            prev.selectedTag !== selectedTag ||
            prev.selectedFolderId !== selectedFolderId ||
            prev.resourceType !== resourceType
        ) {
            SharedAssetsPaneController.setFilters(controllerFilters);
            if (prev.selectedFolderId !== selectedFolderId && selectedFolderId) {
                SharedAssetsPaneController.setSearch({ query: "" });
            }
        }

        prevSearchStateRef.current = { resourceType, selectedTag, selectedFolderId, sort };
    }, [resourceType, selectedTag, selectedFolderId, getObjectHash(sort)]);

    const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.SHARED_RESOURCES,
        PermissionActionType.MANAGE
    );
    const existingTags = tags.map(tag => tag.tag);

    const createOrUpdateTeamAsset = async (
        fromAssetId: string,
        attributesOverrides: { tags?: string[], name?: string, type?: string } = {},
        assetResource: IWorkspaceResourceAsset = null,
        isNewAsset: boolean = false,
    ) => {
        if (assetResource) {
            try {
                await WorkspaceController.updateWorkspaceResource({
                    resourceId: assetResource.resourceId,
                    resourceType: WorkspaceResourceType.ASSET,
                    tags: attributesOverrides?.tags ?? assetResource?.tags ?? [],
                    metadata: {
                        type: attributesOverrides?.type ?? assetResource.metadata.type,
                        createdBy: assetResource.metadata?.createdBy,
                        name: attributesOverrides?.name ?? assetResource.metadata.name,
                        alt: assetResource.metadata.alt,
                        isPublished: assetResource.metadata?.isPublished,
                        actualId: fromAssetId,
                    },
                }, { isNewAsset, isReplacedAsset: !!assetResource });

                const asset = await ds.assets.getAssetById(assetResource.resourceId);
                await asset.reload();
            } catch (error) {
                logger.error(error, "[SharedAssetsPane] Error updating workspace asset");
                throw error;
            }
            return;
        }

        try {
            const asset = await ds.assets.cloneAsset(fromAssetId, {
                userId: null,
                id: `${workspace.id}:${uuid()}`,
            }, false);

            if (!asset?.id) {
                throw new Error("Failed to clone asset: asset is missing or invalid.");
            }

            await WorkspaceController.createWorkspaceResource({
                resource: {
                    resourceId: asset.id,
                    resourceType: WorkspaceResourceType.ASSET,
                    folderIds: [],
                    tags: attributesOverrides?.tags || [],
                    metadata: {
                        type: attributesOverrides?.type ?? asset.get("type"),
                        createdBy: app.user.id,
                        modifiedBy: app.user.id,
                        name: attributesOverrides?.name ?? asset.get("assetName"),
                        alt: asset.get("name") ?? "",
                        isPublished: true,
                        actualId: fromAssetId,
                    },
                },
                defaultPermissions: "shared",
            }, { isNewAsset, assetType: attributesOverrides?.type ?? asset.get("type") }) as IWorkspaceResourceAsset;

            if (selectedFolderId) {
                await WorkspaceController.updateWorkspaceResourceFolderResources({
                    folderId: selectedFolderId,
                    update: {
                        add: [{ resourceId: asset.id, resourceType: WorkspaceResourceType.ASSET, permissions: { read: { object: true, permissions: true }, write: { object: false, permissions: false } } }],
                    },
                });
            }

            await asset.reload();
        } catch (error) {
            logger.error(error, "[SharedAssetsPane] Error cloning and creating workspace asset");
            throw error;
        }
    };

    const handleAddItem = async () => {
        const { createdAssetModel, isNewAsset } = await new Promise<{
            createdAssetModel: { content_type: string, content_value: string, assetName: string, tags: string[] },
            isNewAsset: boolean,
        }>(resolve => {
            ShowDialog(AddAssetsContainer, {
                assetType: AssetType.IMAGE,
                hideTeamAssets: true,
                hideIcons: true,
                hideVideos: true,
                callback: (model: { content_type: string, content_value: string, assetName: string, tags: string[] }, isNew: boolean) => {
                    resolve({ createdAssetModel: model, isNewAsset: isNew });
                },
            });
        });

        if (!createdAssetModel) {
            return;
        }

        ShowDialog(CreateTeamAssetDialog, {
            type: createdAssetModel.content_type,
            name: createdAssetModel.assetName,
            tags: createdAssetModel.tags,
            existingTags,
            callback: async ({ type, name, tags: assetTags }) => {
                try {
                    await createOrUpdateTeamAsset(createdAssetModel.content_value, { type, name, tags: assetTags }, null, isNewAsset);

                    if (type === "logo" && resourceType === TeamResourceTypes.TEAM_ASSETS_IMAGES) {
                        handleSelectResourceType(TeamResourceTypes.SHARED_ASSETS_LOGOS);
                    }
                    if (type === "image" && resourceType === TeamResourceTypes.SHARED_ASSETS_LOGOS) {
                        handleSelectResourceType(TeamResourceTypes.TEAM_ASSETS_IMAGES);
                    }
                } catch (err) {
                    logger.error(err, "[SharedAssetsPane] Error creating team asset", { name });
                    ShowErrorDialog({
                        error: t("An error occurred while creating the team asset."),
                        message: (err as Error).message,
                    });
                }
            },
        });
    };

    const handleUpdateAsset = (teamAsset: IWorkspaceResourceAsset) =>
        new Promise(resolve => {
            const showAssetsDialog = () => {
                ShowDialog(AddAssetsContainer, {
                    assetType: teamAsset.metadata.type,
                    hideTeamAssets: true,
                    hideIcons: true,
                    hideVideos: true,
                    hidePhotos: teamAsset.metadata.type !== AssetType.IMAGE,
                    hideLogos: teamAsset.metadata.type !== AssetType.LOGO,
                    onCancel: () => resolve(false),
                    callback: async (model: { content_type: string, content_value: string }, isNew: boolean) => {
                        try {
                            await createOrUpdateTeamAsset(model.content_value, { type: model.content_type }, teamAsset, isNew);
                            resolve(true);
                        } catch (err) {
                            logger.error(err, "[SharedAssetsPane] Error replacing asset", { id: teamAsset.resourceId });
                            ShowErrorDialog({
                                error: t("An error occurred while updating the asset."),
                                message: (err as Error).message,
                            });
                            resolve(false);
                        }
                    },
                });
            };

            ShowConfirmationDialog({
                title: t("Update Asset"),
                message: t("Update this asset once to update it everywhere it's used. Choose a replacement image or logo, and existing slides that use this asset will update automatically."),
                acceptCallback: showAssetsDialog,
                cancelCallback: () => resolve(false),
            });
        });

    const handleRemoveFromFolder = async (selection: IWorkspaceResourceAsset[]) => {
        if (!selectedFolderId) {
            return;
        }

        const resources = selection.map(asset => ({
            resourceId: asset.resourceId,
            resourceType: asset.resourceType,
        }));

        await removeResourcesFromFolder({
            folderId: selectedFolderId,
            resources,
            resourceLabel: selection.length === 1 ? t("asset") : t("assets"),
        });
    };

    const handleDeleteAssets = async (selection: IWorkspaceResourceAsset[]) => {
        if (await ShowConfirmationDialog({
            title: selection.length === 1
                ? t("Are you sure you want to delete this asset?")
                : t("Are you sure you want to delete {{count}} selected assets?", { count: selection.length }),
            message: t("Slides that already use this asset will not be changed. This action can not be undone."),
            buttonOptions: { cancelButtonVariant: "outlined", acceptButtonColor: "red" },
            okButtonLabel: t("Delete"),
        })) {
            for (const asset of selection) {
                try {
                    await withProgressDialog({
                        title: t("Deleting Team Asset..."),
                        action: () => WorkspaceController.deleteWorkspaceResource({
                            resourceId: asset.resourceId,
                            resourceType: WorkspaceResourceType.ASSET,
                        }),
                    });
                } catch (err) {
                    logger.error(err, "[SharedAssetsPane] delete failed", { resourceId: asset.resourceId });
                    ShowErrorDialog({
                        error: t("An error occurred while deleting the Team Asset."),
                        message: (err as Error).message,
                    });
                }
            }
        }
    };

    const handleEditSettings = async (assetResource: IWorkspaceResourceAsset) => {
        ShowDialog(TeamAssetSettingsDialog, {
            assetResource,
            existingTags,
        });
    };

    const handleSetTags = (selection: IWorkspaceResourceAsset[]) => {
        ShowDialog(BulkSetTagsDialog, {
            selection,
            existingTags,
            onAccept: async (assetTags: string[]) => {
                if (!assetTags.length) {
                    return;
                }
                for (const asset of selection) {
                    try {
                        await WorkspaceController.updateWorkspaceResource({
                            resourceId: asset.resourceId,
                            resourceType: WorkspaceResourceType.ASSET,
                            tags: { add: assetTags },
                        });
                    } catch (err) {
                        logger.error(err, "[SharedAssetsPane] handleSetTags() failed", { id: asset.id });
                    }
                }
            },
        });
    };

    const handleBulkUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Snapshot before clearing the input — FileList is live and becomes empty when value is reset.
        const files = Array.from(event.target.files ?? []);
        if (!files.length) {
            return;
        }
        event.target.value = "";

        const assetType = mediaFilterForResourceType(resourceType) === "logo"
            ? AssetType.LOGO
            : AssetType.IMAGE;

        ShowDialog(BulkAssetUploadDialog, {
            files,
            existingTags,
            types: assetType,
            onAccept: async ({ tags: assetTags, types }: { tags: string[], types: string }) => {
                const progressDialog = ShowDialog(ProgressDialog, {
                    title: t("Uploading Assets..."),
                    progress: 0,
                });
                const failedFiles: string[] = [];

                for (let i = 0; i < files.length; i++) {
                    const file = files[i];
                    progressDialog.setProgress(i / files.length * 100);
                    progressDialog.setMessage(t("Uploading {{fileName}}", { fileName: file.name }));

                    let fileType;
                    if (file.type === "image/jpeg") {
                        fileType = ASSET_FILETYPE.JPG;
                    } else if (file.type === "image/png") {
                        fileType = ASSET_FILETYPE.PNG;
                    } else if (file.type === "image/gif") {
                        fileType = ASSET_FILETYPE.GIF;
                    } else if (file.type === "image/svg+xml") {
                        fileType = ASSET_FILETYPE.SVG;
                    }
                    let currentType = types === AssetType.LOGO ? AssetType.LOGO : AssetType.IMAGE;
                    try {
                        const asset = await ds.assets.getOrCreateImage({
                            file,
                            name: file.name,
                            fileType,
                            assetType: currentType,
                            metadata: { source: "bulk-upload" },
                        });

                        await createOrUpdateTeamAsset(asset.id, { tags: assetTags, name: file.name, type: currentType }, null);
                    } catch (err) {
                        logger.error(err, "[SharedAssetsPane] Bulk upload failed for file", { name: file.name });
                        failedFiles.push(file.name);
                    }
                }

                progressDialog.props.closeDialog();

                if (failedFiles.length > 0) {
                    const message = failedFiles.length === 1
                        ? t("Failed to upload {{fileName}}.", { fileName: failedFiles[0] })
                        : t("Failed to upload {{count}} files.", { count: failedFiles.length });

                    ShowErrorDialog({
                        error: t("Some assets could not be uploaded."),
                        message,
                    });
                }
            },
        });
    };

    const renderContextMenu = (selection: IWorkspaceResourceAsset[]) => {
        if (canUpgradeToManageSharedResources) {
            const menuItems = [];
            if (selection.length === 1) {
                menuItems.push(<MenuItem divider key="edit" disabled><UpgradeIcon /><Gap5 />{t("Edit Asset Properties...")}</MenuItem>);
                menuItems.push(<MenuItem divider key="replace" disabled><UpgradeIcon /><Gap5 />{t("Update Asset...")}</MenuItem>);
            } else {
                menuItems.push(<MenuItem divider key="tags" disabled><UpgradeIcon /><Gap5 />{t("Add tags to {{count}} selected assets...", { count: selection.length })}</MenuItem>);
            }
            if (selectedFolderId) {
                menuItems.push(
                    <MenuItem key="remove-from-folder" divider onClick={() => handleRemoveFromFolder(selection)}>
                        <Icon>remove_circle</Icon>{t("Remove from Folder")}
                    </MenuItem>
                );
            }
            menuItems.push(
                <MenuItem key="trash" onClick={() => handleDeleteAssets(selection)}>
                    <Icon>delete</Icon>
                    {selection.length === 1 ? t("Delete Asset") : t("Delete {{count}} Selected Assets", { count: selection.length })}
                </MenuItem>
            );
            return menuItems;
        }

        const menuItems = [];
        if (selection.length === 1) {
            menuItems.push(
                <MenuItem divider key="edit" onClick={() => handleEditSettings(selection[0])}>
                    <Icon>edit</Icon>{t("Edit Asset Properties...")}
                </MenuItem>,
                <MenuItem divider key="replace" onClick={() => handleUpdateAsset(selection[0])}>
                    <Icon>sync</Icon>{t("Update Asset...")}
                </MenuItem>
            );
            if (selectedFolderId) {
                menuItems.push(
                    <MenuItem key="remove-from-folder" divider onClick={() => handleRemoveFromFolder(selection)}>
                        <Icon>remove_circle</Icon>{t("Remove from Folder")}
                    </MenuItem>
                );
            }
            menuItems.push(
                <MenuItem key="trash" onClick={() => handleDeleteAssets(selection)}>
                    <Icon>delete</Icon>{t("Delete Asset")}
                </MenuItem>
            );
        } else {
            menuItems.push(
                <MenuItem divider key="tags" onClick={() => handleSetTags(selection)}>
                    <Icon>label</Icon>{t("Add tags to {{count}} selected assets...", { count: selection.length })}
                </MenuItem>
            );
            if (selectedFolderId) {
                menuItems.push(
                    <MenuItem key="remove-from-folder" divider onClick={() => handleRemoveFromFolder(selection)}>
                        <Icon>remove_circle</Icon>
                        {t("Remove {{count}} from Folder", { count: selection.length })}
                    </MenuItem>
                );
            }
            menuItems.push(
                <MenuItem key="trash" onClick={() => handleDeleteAssets(selection)}>
                    <Icon>delete</Icon>{t("Delete {{count}} Selected Assets", { count: selection.length })}
                </MenuItem>
            );
        }
        return menuItems;
    };

    const handleFolderDrop = useCallback(async (folder: IWorkspaceResourceFolder) => {
        const items = getActiveDragItems();
        if (!items.length) {
            return;
        }

        if (folder.id !== selectedFolderId) {
            setSelectedFolderId(folder.id);
            onSelectFolder?.(resourceType, folder.id);
        }

        clearDragItems();

        await moveResourcesToFolder({
            folder,
            resources: items,
            progressTitle: t("Moving asset to folder..."),
            progressTitlePlural: count => t("Moving {{count}} assets to folder...", { count }),
            errorMessage: t("An error occurred while moving assets to the folder."),
            logTag: "SharedAssetsPane"
        });
    }, [clearDragItems, getActiveDragItems, onSelectFolder, resourceType, selectedFolderId]);

    const handleThumbnailVisibilityChange = (isVisible: boolean, item: IWorkspaceResourceAsset) => {
        if (!isVisible || !SharedAssetsPaneController.canLoadMore) {
            return;
        }

        const index = data.findIndex(({ resource }) => resource.id === item.id);
        if (index === data.length - 1) {
            SharedAssetsPaneController.loadMore();
        }
    };

    const handleSelectResourceType = useCallback((nextResourceType: string) => {
        setResourceType(nextResourceType);
        onSelectFolder?.(nextResourceType, undefined);
        setSelectedFolderId(null);
        if (nextResourceType !== TeamResourceTypes.TEAM_ASSETS) {
            SharedAssetsPaneController.setSearch({ query: "" });
        }
    }, [onSelectFolder]);

    const handleNavigateToFolder: SharedResourceFolderNavigateHandler = useCallback(({ folderId: nextFolderId, parentFolderId }) => {
        const nextSelectedFolderId = parentFolderId ?? nextFolderId;
        setSelectedFolderId(nextSelectedFolderId);
        setResourceType(TeamResourceTypes.TEAM_ASSETS);
        SharedAssetsPaneController.setSearch({ query: "" });
        onSelectFolder?.(TeamResourceTypes.TEAM_ASSETS, nextSelectedFolderId);
    }, [onSelectFolder]);

    const handleSearch = (searchTerm: string) => {
        SharedAssetsPaneController.setSearch({ query: searchTerm?.trim().toLowerCase() });
        handleSelectResourceType(TeamResourceTypes.TEAM_ASSETS);
    };

    const handleSort = (nextSort: SearchBarSort) => {
        setSort(nextSort);
    };

    const handleSelectTag = (event: { target: { value: string } }) => {
        setSelectedTag(event.target.value);
    };

    const handleSelectSidebarFolder = useCallback((nextFolderId: string | null) => {
        setSelectedFolderId(nextFolderId);
        SharedAssetsPaneController.setSearch({ query: "" });
        setResourceType(TeamResourceTypes.TEAM_ASSETS);
        onSelectFolder?.(TeamResourceTypes.TEAM_ASSETS, nextFolderId ?? undefined);
    }, [onSelectFolder]);

    const filteredAssets = data.map(record => record.resource);
    const shouldHideTopLevelSideBar = filteredAssets.length === 0 &&
        !search.query &&
        !selectedFolderId &&
        selectedTag === TAG_FILTER_ALL &&
        resourceType === TeamResourceTypes.TEAM_ASSETS;
    const shouldRenderSideBar = isInitializedForCurrentMount && !shouldHideTopLevelSideBar;

    const sideBarCategoryNav = useMemo(() => (
        <>
            <SideBarItem
                className={selectedClass(resourceType === TeamResourceTypes.TEAM_ASSETS && selectedFolderId === null)}
                onClick={() => handleSelectResourceType(TeamResourceTypes.TEAM_ASSETS)}
                id="all-assets"
            >
                <Icon className="material-symbols-outlined">select_all</Icon>
                {t("All assets")}
            </SideBarItem>
            <SideBarItem
                className={selectedClass(resourceType === TeamResourceTypes.TEAM_ASSETS_IMAGES && selectedFolderId === null)}
                onClick={() => handleSelectResourceType(TeamResourceTypes.TEAM_ASSETS_IMAGES)}
                id="asset-photos"
            >
                <Icon className="material-symbols-outlined">photo_camera</Icon>
                {t("Photos")}
            </SideBarItem>
            <SideBarItem
                className={selectedClass(resourceType === TeamResourceTypes.SHARED_ASSETS_LOGOS && selectedFolderId === null)}
                onClick={() => handleSelectResourceType(TeamResourceTypes.SHARED_ASSETS_LOGOS)}
                id="asset-logos"
            >
                <Icon className="material-symbols-outlined">sort_by_alpha</Icon>
                {t("Logos")}
            </SideBarItem>
        </>
    ), [handleSelectResourceType, resourceType, selectedFolderId]);

    const renderAssets = () => {
        const isLoadingMore = fetching && filteredAssets.length > 0;
        const isLoadingInitial = fetching && filteredAssets.length === 0;

        if (isLoadingInitial || !SharedAssetsPaneController.initialized) {
            return <Spinner />;
        }

        if (!filteredAssets.length) {
            if (selectedFolderId) {
                return (<SharedSlideFolderNotice hideMessage={!editable} />);
            }

            if (
                !search.query &&
                !selectedFolderId &&
                selectedTag === TAG_FILTER_ALL
            ) {
                if (resourceType === TeamResourceTypes.TEAM_ASSETS_IMAGES) {
                    return (<SharedSlideFolderNotice
                        title={t("You don’t have any images yet.")}
                        message={t("Create a library of shared assets to keep your team on-brand and up to date. Updates will apply anywhere it's used.")}
                    />);
                }
                if (resourceType === TeamResourceTypes.SHARED_ASSETS_LOGOS) {
                    return (<SharedSlideFolderNotice
                        title={t("You don’t have any logos yet.")}
                        message={t("Create a library of shared assets to keep your team on-brand and up to date. Updates will apply anywhere it's used.")}
                    />);
                }
                return <NoTeamAssetNotice />;
            }
            return <NoMatchNotice />;
        }

        return (
            <MultiSelectThumbnailGrid
                {...teamResourceGridProps}
                items={filteredAssets}
                readOnly={!editable}
                allowDragging={editable && canUseSharedResources}
                allowMultiSelect={editable && canUseSharedResources}
                thumbnailClass={AssetThumbnail}
                thumbnailProps={{
                    canUpgradeToManageSharedResources,
                    onVisibleChange: handleThumbnailVisibilityChange,
                    resourceFolders,
                    onNavigateToFolder: handleNavigateToFolder,
                }}
                onStartDragItems={handleStartDragItems}
                onDropDragItems={handleDropDragItems}
                onItemSelected={selection => {
                    if (canUpgradeToManageSharedResources) {
                        return;
                    }
                    if (addAssetCallback) {
                        addAssetCallback(selection[0]);
                        handleConfirm?.();
                    }
                }}
                onDoubleClick={
                    canUpgradeToManageSharedResources
                        ? undefined
                        : addAssetCallback
                            ? asset => {
                                addAssetCallback(asset);
                                handleConfirm?.();
                            }
                            : asset => handleEditSettings(asset)
                }
                renderContextMenuItems={editable ? renderContextMenu : undefined}
                isLoadingMore={isLoadingMore}
            />
        );
    };

    const title = useMemo(() => {
        if (resourceType === TeamResourceTypes.TEAM_ASSETS_IMAGES) {
            return t("Photos");
        }
        if (resourceType === TeamResourceTypes.SHARED_ASSETS_LOGOS) {
            return t("Logos");
        }
        return t("Assets");
    }, [resourceType]);

    const handleBreadcrumbSelectFolder = (nextFolderId: string | null) => {
        setSelectedFolderId(nextFolderId);
        SharedAssetsPaneController.setSearch({ query: "" });
        setResourceType(TeamResourceTypes.TEAM_ASSETS);
        onSelectFolder?.(TeamResourceTypes.TEAM_ASSETS, nextFolderId ?? undefined);
    };

    const breadcrumb = useMemo(() => buildResourcePaneBreadcrumbData({
        rootLabel: title,
        selectedFolderId,
        resourceFolders,
        onSelectFolder: handleBreadcrumbSelectFolder,
    }), [title, selectedFolderId, resourceFolders]);

    return (
        <Layout>
            {canUseSharedResources && (<>
                <SearchControls>
                    <SearchAndAdd
                        onSearch={handleSearch}
                        searchValue={search.query}
                        searchPlaceholder={t("Search team assets...")}
                        tagFilterOptions={tagFilterOptions}
                        selectedTagFilter={selectedTag}
                        onTagFilterChange={handleSelectTag}
                    />
                    {editable && (
                        <AssetActions>
                            <BulkUploadButton
                                iconColor="#666"
                                large
                                onClick={() => bulkUploadInputRef.current?.click()}
                            >
                                <BaiIcon>arrow_circle_up</BaiIcon>
                                {t("Bulk Upload")}
                            </BulkUploadButton>
                            <CreateAssetButton blue large onClick={handleAddItem}>
                                <BaiIcon white fill>add_circle</BaiIcon>
                                {t("New Asset")}
                            </CreateAssetButton>
                            <UploadFileInput
                                ref={bulkUploadInputRef}
                                type="file"
                                multiple
                                accept={"image/png,image/jpeg,image/gif,image/svg+xml"}
                                onChange={handleBulkUpload}
                            />
                        </AssetActions>
                    )}
                </SearchControls>
            </>)}

            {canUpgradeToManageSharedResources && (
                <FlexBox center fillWidth>
                    <Banner
                        headerText={t("Unlock Team Assets")}
                        bodyText={t("Upgrade to a Team plan to centralize approved brand assets.")}
                        buttonLabel={t("Upgrade")}
                        onButtonClick={() => ShowDialog(ChangePlanDialog,
                            {
                                featuredPlan: WorkspacePlanTier.TEAM,
                                analytics: { cta: "unlock_team_assets" }
                            })}
                    />
                </FlexBox>
            )}

            <PaneBody>
                <ResourcePaneSideBar
                    visible={shouldRenderSideBar}
                    readOnly={!editable}
                    resourceTypes={[WorkspaceResourceType.ASSET]}
                    folderType={WorkspaceResourceFolderType.SHARED}
                    createResourcesType={WorkspaceResourceType.ASSET}
                    resourceFolders={resourceFolders}
                    selectedFolderId={selectedFolderId}
                    draggedItems={draggedItems}
                    onFolderDrop={handleFolderDrop}
                    onSelectFolder={handleSelectSidebarFolder}
                >
                    {sideBarCategoryNav}
                </ResourcePaneSideBar>
                <PaneContent>
                    <ListHeaderRow>
                        <PresentationLibraryPaneBreadcrumb {...breadcrumb} />
                        {canUseSharedResources && (
                            <PresentationListHeader
                                sortOptions={sortOptions}
                                sort={sort}
                                searchQuery={search.query ?? ""}
                                onSetSort={handleSort}
                                showViewTypeToggle={false}
                            />
                        )}
                    </ListHeaderRow>
                    <ResultsContainer>
                        {renderAssets()}
                    </ResultsContainer>
                </PaneContent>
            </PaneBody>
        </Layout>
    );
}

export default WorkspaceController.withInitializedState(SharedAssetsPaneController.withState(SharedAssetsPane));
